import { Fragment, memo, useCallback, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "shared/components/ui/table";
import HeaderIndicator from "./HeaderIndicator";

import styles from "../../styles.module.scss";
import { ProductData, PortfolioTableProps } from "../../types";
import { updateGlobalTooltipText } from "../../utils";
import { useFeeType } from "pages/assistant/hooks/useFeeType";
import activityPanelStore from "shared/store/activityPanelStore";
import { createLogger } from "shared/logger";
import { useFamFofExpansion } from "../../../fineco-portfolio/components/composizione-del-portafoglio/components/hooks/useFamFofExpansion";
import { FamFofConstituents } from "../../../fineco-portfolio/components/composizione-del-portafoglio/components/FamFofConstituents";

const log = createLogger("PortfolioTable");

// Memoized complete table component
const PortfolioTable = memo(
  ({
    data,
    headers,
    sortConfig,
    columnMappings,
    columnWidths,
    colorsConfig,
    handleSort,
    onTooltipTrigger,
    onTooltipHide,
    sortableColumns,
    totalControvalore,
    pinnedColumns = [],
  }: PortfolioTableProps) => {
    const feeType = useFeeType();
    const constraintsPanelMode = activityPanelStore((state) => state.constraintsPanelMode);
    const isRebalancing = constraintsPanelMode !== 'builder';

    const totalWidth = useMemo(() => columnWidths.reduce((sum, w) => sum + w, 0), [columnWidths]);

    const getPinnedStyle = useCallback(
      (header: string) => {
        const pinnedIndex = pinnedColumns.indexOf(header);
        if (pinnedIndex === -1) return {};

        let leftOffset = 0;
        for (let i = 0; i < pinnedIndex; i++) {
          const prevHeader = pinnedColumns[i];
          const prevIndex = headers.indexOf(prevHeader);
          if (prevIndex !== -1) leftOffset += columnWidths[prevIndex];
        }

        const isLastPinned = pinnedIndex === pinnedColumns.length - 1;
        return {
          position: 'sticky' as const,
          left: `${leftOffset}px`,
          zIndex: 3,
          backgroundColor: 'var(--FinecoWhite, #fff)',
          ...(isLastPinned && {
            boxShadow: '10px 0 10px -10px rgba(0,0,0,0.15)',
          }),
        };
      },
      [pinnedColumns, headers, columnWidths],
    );

    // Table styles
    const tableStyles = {
      customTable: {
        borderCollapse: "collapse" as const,
        width: `${totalWidth}px`,
      },
    };

    const formatCellValue = useCallback(
      (key: string, product: ProductData): string => {
        if (key === "final_score_advice") {
          if (isRebalancing && feeType === 'feeOnTop') {
            return String(product["final_score"]);
          }
          if (isRebalancing && feeType === 'feeOnly') {
            return String(product["final_score_advice"]);
          }
          return product["final_score"] + "/" + product[key];
        }
        return String(product[key] ?? "-");
      },
      [isRebalancing, feeType],
    );

    // FAM FoF constituents widget (issue #90), same as the portfolio
    // artifact's composition tables: FAM fund-of-funds rows get an expand
    // button revealing «Composizione del fondo».
    const { famParents, expanded, toggle } = useFamFofExpansion();

    const handleCellClick = useCallback((text: string) => {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          updateGlobalTooltipText("Copied");
        })
        .catch((err) => {
          log.warn("copy to clipboard failed", { error: err });
        });
    }, []);

    return (
      <>
        <style>
          {`
          .customTable {
            table-layout: fixed;
          }
          .customTable th {
            border-bottom: none !important;
            padding: 8px 16px;
            position: relative;
          }

          .customTable tbody tr:first-child td {
            border-top: none !important;
            padding-top: 8px !important;
          }
        `}
        </style>
        <Table
          className={`${styles.table} customTable`}
          style={tableStyles.customTable}
        >
          <TableHeader>
            <TableRow>
              {headers.map((header, index) => {
                let textAlignment = "center";


                const isSortable = sortableColumns.includes(header);
                const specialClass = isSortable ? "specialBorder" : "";
                return (
                  <TableHead
                    key={index}
                    className={specialClass}
                    onClick={isSortable ? () => handleSort(header) : undefined}
                    style={{
                      fontWeight: "bold",
                      // width: `${columnWidths[index]}%`,
                      width: `${columnWidths[index]}px`,
                      minWidth: `${columnWidths[index]}px`,
                      maxWidth: `${columnWidths[index]}px`,
                      textAlign: textAlignment as any,
                      cursor: isSortable ? "pointer" : "default",
                      ...getPinnedStyle(header),
                    }}
                  >
                    {columnMappings[header] || header}
                    {isSortable && (
                      <HeaderIndicator color={colorsConfig.FinecoBluePrimary} />
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((product, index) => {
              const rowIsin = String(product.isin ?? "");
              const isFamFof = rowIsin !== "" && famParents.has(rowIsin);
              const expansion = isFamFof ? expanded[rowIsin] : undefined;
              return (
                <Fragment key={index}>
                  <TableRow>
                    {headers.map((key, cellIndex) => {
                      let textAlignment = "center";

                      const valueToRender = formatCellValue(key, product);

                      return (
                        <TableCell
                          key={cellIndex}
                          onMouseEnter={(e) =>
                            onTooltipTrigger(
                              e,
                              String(product[key] ?? "-"),
                              key === "isin" || key === "nome" ? product.market_tooltip : undefined,
                            )
                          }
                          onMouseLeave={onTooltipHide}
                          onClick={() => handleCellClick(String(product[key] ?? "-"))}
                          style={{
                            // width: `${columnWidths[cellIndex]}%`,
                            width: `${columnWidths[cellIndex]}px`,
                            minWidth: `${columnWidths[cellIndex]}px`,
                            maxWidth: `${columnWidths[cellIndex]}px`,
                            textAlign: textAlignment as any,
                            ...getPinnedStyle(key),
                          }}
                        >
                          {key === "isin" && isFamFof ? (
                            <span className={styles.famFofCell}>
                              <button
                                type="button"
                                className={styles.famFofToggle}
                                aria-expanded={!!expansion}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggle(rowIsin);
                                }}
                              >
                                {expansion ? "−" : "+"}
                              </button>
                              <span className={styles.famFofIsin}>{valueToRender}</span>
                            </span>
                          ) : (
                            valueToRender
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {expansion && (
                    <TableRow>
                      <TableCell
                        colSpan={headers.length}
                        className={styles.famFofInsetCell}
                      >
                        <div className={styles.famFofInset}>
                          <FamFofConstituents state={expansion} />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {totalControvalore && (
              <TableRow className={styles.totalRow}>
                {headers.map((header, index) => {
                  let content = "";
                  // Find the controvalore column index dynamically
                  const controvaloreIndex = headers.indexOf("controvalore");

                  if (controvaloreIndex !== -1 && index === controvaloreIndex - 1) {
                    // Column before controvalore - show "TOTALE"
                    content = "TOTALE";
                  } else if (index === controvaloreIndex && controvaloreIndex !== -1) {
                    // Controvalore column - show total
                    content = totalControvalore;
                  }

                  return (
                    <TableCell
                      key={index}
                      style={{
                        width: `${columnWidths[index]}px`,
                        minWidth: `${columnWidths[index]}px`,
                        maxWidth: `${columnWidths[index]}px`,
                        textAlign: "center",
                        fontWeight: "bold",
                        ...getPinnedStyle(headers[index]),
                      }}
                    >
                      {content}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if data, sort configuration, column mappings, or total changes
    return (
      prevProps.data === nextProps.data &&
      prevProps.sortConfig.key === nextProps.sortConfig.key &&
      prevProps.sortConfig.direction === nextProps.sortConfig.direction &&
      prevProps.totalControvalore === nextProps.totalControvalore &&
      prevProps.columnMappings === nextProps.columnMappings
    );
  },
);

export default PortfolioTable;
